import { createRoutine } from 'redux-saga-routines'
import {
  call,
  debounce,
  delay,
  fork,
  put,
  select,
  take,
  takeEvery,
  spawn,
  takeLatest,
  takeLeading
} from '@redux-saga/core/effects'
import { eventChannel } from 'redux-saga'
import { head, pluck } from 'ramda'
import { isFalsy } from 'ramda-adjunct'
import ExchangeService from 'src/services/ExchangeService'
import DictionariesService from 'src/services/DictionariesService'
import LaravelEchoService from 'src/services/LaravelEchoService'
import {
  ERROR_DELAY_TIME,
  EXCHANGE_TYPES,
  EXCHANGE_EVENT_TYPES
} from 'src/ducks/consts'
import { selectDictionaries } from 'src/ducks/selectors'
import { selectCurrentUserId } from 'src/features/account/duck/selectors'
import {
  selectImportActiveTab,
  selectImportFilters,
  selectImportFiltersFormatted,
  selectImportFiltersTabsList,
  selectIsImportOffersLoading,
  selectImportTabsData
} from 'src/features/stocks/ducks/selectors'
import { setSnackbarValues } from 'src/ducks/actions'
import { showAlertToast } from 'src/components/Toast'
import translate from 'src/intl/translate'
import {
  displayResponseErrorMessage,
  getErrorMessageFromApiResponse
} from 'src/utils/helpers'
import { normalizeToLocationsIds, normalizeToCompaniesIds } from './normalizers'
import {
  clearOfferList,
  getOfferListRollbackView,
  isOfferListRateLimitActive,
  registerOfferListRateLimit,
  restoreOfferListView
} from './offerListRateLimit'

export const getImportOffersRoutine = createRoutine('GET_IMPORT_OFFERS')
export const clearImportOffersRoutine = createRoutine('CLEAR_IMPORT_OFFERS')
export const cleanImportOffersDetailsRoutine = createRoutine(
  'CLEAN_IMPORT_OFFERS_DETAILS'
)
export const showImportOfferDetailsRoutine = createRoutine(
  'SHOW_IMPORT_OFFER_DETAILS'
)
export const setImportDrawerRoutine = createRoutine('SET_IMPORT_DRAWER')
export const setImportFiltersRoutine = createRoutine('SET_IMPORT_FILTERS')
export const updateImportOfferRoutine = createRoutine('UPDATE_IMPORT_OFFER')
export const reserveImportOfferRoutine = createRoutine('RESERVE_IMPORT_OFFER')
export const bidImportOfferRoutine = createRoutine('BID_IMPORT_OFFER')
export const refreshImportOfferRoutine = createRoutine('REFRESH_IMPORT_OFFER')
export const makeImportOfferPublicRoutine = createRoutine(
  'MAKE_IMPORT_OFFER_PUBLIC'
)
export const clearImportFiltersRoutine = createRoutine('CLEAR_IMPORT_FILTERS')
export const getImportFilterTabsListRoutine = createRoutine(
  'GET_IMPORT_FILTER_TABS_LIST'
)
export const createImportFilterTabRoutine = createRoutine(
  'CREATE_IMPORT_FILTER_TAB'
)
export const removeImportFilterTabRoutine = createRoutine(
  'REMOVE_IMPORT_FILTER_TAB'
)
export const setImportActiveTabRoutine = createRoutine('SET_IMPORT_ACTIVE_TAB')

export const IMPORT_TAB_EVENT_OFFER_CREATED = 'IMPORT_TAB_EVENT_OFFER_CREATED'
export const IMPORT_TAB_EVENT_OFFER_REFRESHED =
  'IMPORT_TAB_EVENT_OFFER_REFRESHED'
export const IMPORT_TAB_EVENT_OFFER_DELETED = 'IMPORT_TAB_EVENT_OFFER_DELETED'
export const IMPORT_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA =
  'IMPORT_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA'
export const importOffersCleanerRoutine = createRoutine('IMPORT_OFFERS_CLEANER')

const IMPORT_FILTERS_CHANGED = 'IMPORT_FILTERS_CHANGED'
const FILTERS_DEBOUNCE_MS = 400
let importFiltersRevision = 0
let importFiltersRollbackView = null
let importFiltersRequestInProgress = false

const invalidateImportFiltersDebounce = () => {
  importFiltersRevision += 1
  importFiltersRollbackView = null
}

//actions

function* getImportOffers() {
  const isLoading = yield select(selectIsImportOffersLoading)

  if (isLoading || isOfferListRateLimitActive()) {
    return { ignored: true }
  }
  try {
    const activeTab = yield select(selectImportActiveTab)

    //case when initially loads
    if (isFalsy(activeTab)) {
      yield call(getImportFilterTabsList)
      const tabs = yield select(selectImportFiltersTabsList)
      return yield call(setImportActiveTab, {
        payload: { id: head(tabs).id }
      })
    }

    yield put(getImportOffersRoutine.request())

    const tabs = yield select(selectImportFiltersTabsList)
    const filters = yield select(selectImportFiltersFormatted)
    const requestedView = {
      activeTab,
      filters: yield select(selectImportFilters)
    }

    const data = yield call(
      ExchangeService.getImportOffersListByTab,
      isFalsy(activeTab) ? head(tabs).id : activeTab,
      filters
    )

    yield put(getImportOffersRoutine.success(data))
    importFiltersRollbackView = requestedView
    yield call(getImportFilterTabsList)

    return { success: true }
  } catch (e) {
    const rateLimit = registerOfferListRateLimit(e)

    if (rateLimit) {
      yield put(getImportOffersRoutine.failure())
      yield call(
        showAlertToast,
        translate().formatMessage(
          { id: 'offers.offerListRateLimited' },
          { seconds: rateLimit.retryAfter }
        ),
        { id: 'offer-list-rate-limit', duration: 5000 }
      )

      return { rateLimited: true }
    }

    yield put(getImportOffersRoutine.failure())
    console.log(e)

    return { failed: true }
  }
}

function* clearImportOffers() {
  yield put(clearImportOffersRoutine.success())
}

function* cleanImportOffersDetails() {
  yield put(cleanImportOffersDetailsRoutine.success())
}

function* showImportOfferDetails({ payload }) {
  yield put(showImportOfferDetailsRoutine.request())
  try {
    const { data } = yield call(ExchangeService.showImportOffer, payload)
    yield put(showImportOfferDetailsRoutine.success(data))
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(showImportOfferDetailsRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(showImportOfferDetailsRoutine.fulfill())
  }
}

function* setImportDrawer({ payload }) {
  yield put(setImportDrawerRoutine.success(payload))
}

function* setImportFilters({ payload }) {
  if (isOfferListRateLimitActive()) {
    return
  }

  const dictionaries = yield select(selectDictionaries)
  const activeTab = yield select(selectImportActiveTab)

  if (!importFiltersRollbackView) {
    importFiltersRollbackView = {
      activeTab,
      filters: yield select(selectImportFilters)
    }
  }

  importFiltersRevision += 1
  const revision = importFiltersRevision

  yield put(setImportFiltersRoutine.success({ ...payload, dictionaries }))
  yield put({
    type: IMPORT_FILTERS_CHANGED,
    payload: { activeTab, revision }
  })
}

function* getImportOffersAfterFiltersChange({ payload }) {
  if (
    payload.revision !== importFiltersRevision ||
    payload.activeTab !== (yield select(selectImportActiveTab))
  ) {
    return
  }

  while (
    importFiltersRequestInProgress ||
    (yield select(selectIsImportOffersLoading))
  ) {
    yield delay(50)
  }

  if (
    payload.revision !== importFiltersRevision ||
    payload.activeTab !== (yield select(selectImportActiveTab))
  ) {
    return
  }

  const rollbackView = importFiltersRollbackView

  if (isOfferListRateLimitActive()) {
    if (rollbackView) {
      yield put(
        restoreOfferListView(
          EXCHANGE_TYPES.IMPORT,
          rollbackView.activeTab,
          rollbackView.filters
        )
      )
    }
    importFiltersRollbackView = null
    return
  }

  importFiltersRequestInProgress = true

  try {
    const result = yield* getImportOffers()

    if (result?.rateLimited && rollbackView) {
      yield put(
        restoreOfferListView(
          EXCHANGE_TYPES.IMPORT,
          rollbackView.activeTab,
          rollbackView.filters
        )
      )
    }
  } finally {
    importFiltersRequestInProgress = false
    if (payload.revision === importFiltersRevision) {
      importFiltersRollbackView = null
    }
  }
}

function* clearImportFilters() {
  const isLoading = yield select(selectIsImportOffersLoading)

  if (isLoading || isOfferListRateLimitActive()) {
    return
  }

  const previousActiveTab = yield select(selectImportActiveTab)
  const previousFilters = yield select(selectImportFilters)
  const rollbackView = getOfferListRollbackView(
    importFiltersRollbackView,
    previousActiveTab,
    previousFilters
  )

  invalidateImportFiltersDebounce()

  yield put(clearImportFiltersRoutine.success())
  const result = yield* getImportOffers()

  if (result?.rateLimited) {
    yield put(
      restoreOfferListView(
        EXCHANGE_TYPES.IMPORT,
        rollbackView.activeTab,
        rollbackView.filters
      )
    )
  }
}

function* updateImportOffer({ payload }) {
  yield put(updateImportOfferRoutine.request())
  try {
    const { data } = yield call(
      ExchangeService.updateImportOffer,
      payload.id,
      payload.values
    )
    yield put(updateImportOfferRoutine.success(data))
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(updateImportOfferRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(updateImportOfferRoutine.fulfill())
  }
}

function* reserveImportOffer({ payload }) {
  yield put(reserveImportOfferRoutine.request())
  try {
    yield call(ExchangeService.reserveImportOffer, payload.id)
    yield put(reserveImportOfferRoutine.success())
    yield* showImportOfferDetails({ payload })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(reserveImportOfferRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(reserveImportOfferRoutine.fulfill())
  }
}

function* bidImportOffer({ payload }) {
  yield put(bidImportOfferRoutine.request())
  try {
    yield call(ExchangeService.bidImportOffer, payload.id, {
      price: payload.price
    })
    yield put(bidImportOfferRoutine.success())
    yield call(showImportOfferDetails, { payload: { id: payload.id } })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'makeABid.successMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(bidImportOfferRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(bidImportOfferRoutine.fulfill())
  }
}

function* refreshImportOffer({ payload }) {
  yield put(refreshImportOfferRoutine.request())
  try {
    yield call(ExchangeService.refreshImportOffer, payload.id)
    yield put(refreshImportOfferRoutine.success())
    yield* showImportOfferDetails({ payload })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.refreshOfferSuccessMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(refreshImportOfferRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(refreshImportOfferRoutine.fulfill())
  }
}

function* makeImportOfferPublic({ payload }) {
  yield put(makeImportOfferPublicRoutine.request())
  try {
    yield call(ExchangeService.makeImportOfferPublic, payload.id)
    yield put(makeImportOfferPublicRoutine.success())
    yield* showImportOfferDetails({ payload })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.makeOfferPublicSuccessMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(makeImportOfferPublicRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(makeImportOfferPublicRoutine.fulfill())
  }
}

function listenToExchangeTabEvents(data) {
  return eventChannel(emitter => {
    LaravelEchoService.getInstance().listenToExchangeTabs({
      exchangeType: data.exchangeType,
      userId: data.userId,
      tabsIds: data.tabsIds,
      callback: (tabId, event, payload) => {
        switch (event) {
          case EXCHANGE_EVENT_TYPES.OFFER_CREATED:
            return emitter({
              type: IMPORT_TAB_EVENT_OFFER_CREATED,
              tabId,
              payload
            })
          case EXCHANGE_EVENT_TYPES.OFFER_REFRESHED:
            return emitter({
              type: IMPORT_TAB_EVENT_OFFER_REFRESHED,
              tabId,
              payload
            })
          case EXCHANGE_EVENT_TYPES.OFFER_DELETED:
            return emitter({
              type: IMPORT_TAB_EVENT_OFFER_DELETED,
              tabId,
              payload
            })
          default:
            //handle information about not handled type
            return null
        }
      }
    })
    // unsubscribe function
    return () => {
      // do whatever to interrupt the socket communication here
    }
  })
}

export function* getImportFilterTabsList() {
  try {
    const tabs = yield call(ExchangeService.getImportFilterTabsList)
    const locationsIds = normalizeToLocationsIds(tabs.data)
    const companiesIds = normalizeToCompaniesIds(tabs.data)

    const locations = yield call(DictionariesService.locations, {
      filter: {
        id: locationsIds
      }
    })
    const companies = yield call(DictionariesService.companies, {
      filter: {
        id: companiesIds
      }
    })

    yield put(
      getImportFilterTabsListRoutine.success({
        data: tabs.data,
        locations: locations.data,
        companies: companies.data
      })
    )

    //listen to tabs events
    const userId = yield select(selectCurrentUserId)
    yield spawn(listenToExchangeTabEventsWatcher, {
      exchangeType: EXCHANGE_TYPES.IMPORT,
      userId,
      tabsIds: pluck('id')(tabs.data)
    })
  } catch (e) {
    console.log(e)
  }
}

function* createImportFilterTab({ payload }) {
  try {
    invalidateImportFiltersDebounce()
    const filters = yield select(selectImportFilters)
    const activeTab = yield select(selectImportActiveTab)
    const tabs = yield select(selectImportFiltersTabsList)
    const activeTabData = head(tabs.filter(v => v.id === activeTab))

    const { data } = yield call(ExchangeService.createImportFilterTab, {
      name: payload.empty ? null : filters.tabName
    })
    yield put(
      createImportFilterTabRoutine.success({
        data,
        currentFilters: payload.empty ? null : activeTabData.currentFilters
      })
    )
    //set active new tab

    yield call(setImportActiveTab, { payload: { id: data.id } })
  } catch (e) {
    console.log(e)
  }
}

function* removeImportFilterTab({ payload }) {
  try {
    const isLoading = yield select(selectIsImportOffersLoading)

    if (isLoading || isOfferListRateLimitActive()) {
      return
    }

    const activeTab = yield select(selectImportActiveTab)
    invalidateImportFiltersDebounce()

    yield call(ExchangeService.removeImportFilterTab, payload.id)
    yield put(removeImportFilterTabRoutine.success({ id: payload.id }))
    //after removal set active tab to first one
    const tabs = yield select(selectImportFiltersTabsList)
    yield call(setImportActiveTab, {
      payload: {
        id: head(tabs).id,
        clearOnRateLimit: activeTab === payload.id
      }
    })
  } catch (e) {
    console.log(e)
  }
}

function* setImportActiveTab({ payload }) {
  try {
    const isLoading = yield select(selectIsImportOffersLoading)
    const previousActiveTab = yield select(selectImportActiveTab)

    if (
      isLoading ||
      isOfferListRateLimitActive() ||
      previousActiveTab === payload.id
    ) {
      return
    }
    const previousFilters = yield select(selectImportFilters)
    const rollbackView = getOfferListRollbackView(
      importFiltersRollbackView,
      previousActiveTab,
      previousFilters
    )

    invalidateImportFiltersDebounce()
    const tabs = yield select(selectImportFiltersTabsList)
    const tab = head(tabs.filter(v => v.id === payload.id))
    const tabsData = yield select(selectImportTabsData)

    yield put(
      setImportActiveTabRoutine.success({
        activeTab: tab.id,
        filters: tab.currentFilters
      })
    )
    const result = yield call(getImportOffers)

    if (result?.rateLimited) {
      if (payload.clearOnRateLimit) {
        yield put(clearOfferList(EXCHANGE_TYPES.IMPORT))
      } else {
        yield put(
          restoreOfferListView(
            EXCHANGE_TYPES.IMPORT,
            rollbackView.activeTab,
            rollbackView.filters
          )
        )
      }

      return result
    }

    //if tab has live data stored move them to offers list
    if (!isFalsy(tabsData[tab.id])) {
      yield put({
        type: IMPORT_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA,
        payload: { tabId: tab.id, tabData: tabsData[tab.id] }
      })
    }

    return result
  } catch (e) {
    console.log(e)
  }
}

function* importOffersCleaner() {
  yield put(importOffersCleanerRoutine.success())
}

function* getImportOffersManually() {
  const activeTab = yield select(selectImportActiveTab)
  const filters = yield select(selectImportFilters)
  const rollbackView = getOfferListRollbackView(
    importFiltersRollbackView,
    activeTab,
    filters
  )

  invalidateImportFiltersDebounce()
  const result = yield* getImportOffers()

  if (result?.rateLimited) {
    yield put(
      restoreOfferListView(
        EXCHANGE_TYPES.IMPORT,
        rollbackView.activeTab,
        rollbackView.filters
      )
    )
  }
}

//watchers

function* getImportOffersRoutineWatcher() {
  yield takeLeading(getImportOffersRoutine.TRIGGER, getImportOffersManually)
}

function* clearImportOffersRoutineWatcher() {
  yield takeLatest(clearImportOffersRoutine.TRIGGER, clearImportOffers)
}

function* cleanImportOffersDetailsRoutineWatcher() {
  yield takeLatest(
    cleanImportOffersDetailsRoutine.TRIGGER,
    cleanImportOffersDetails
  )
}

function* showImportOfferDetailsRoutineWatcher() {
  yield takeLatest(
    showImportOfferDetailsRoutine.TRIGGER,
    showImportOfferDetails
  )
}

function* setImportDrawerRoutineWatcher() {
  yield takeLatest(setImportDrawerRoutine.TRIGGER, setImportDrawer)
}

function* setImportFiltersRoutineWatcher() {
  yield takeEvery(setImportFiltersRoutine.TRIGGER, setImportFilters)
}

function* importFiltersDebounceWatcher() {
  yield debounce(
    FILTERS_DEBOUNCE_MS,
    IMPORT_FILTERS_CHANGED,
    getImportOffersAfterFiltersChange
  )
}

function* updateImportOfferRoutineWatcher() {
  yield takeLatest(updateImportOfferRoutine.TRIGGER, updateImportOffer)
}

function* reserveImportOfferRoutineWatcher() {
  yield takeLatest(reserveImportOfferRoutine.TRIGGER, reserveImportOffer)
}

function* bidImportOfferRoutineWatcher() {
  yield takeLatest(bidImportOfferRoutine.TRIGGER, bidImportOffer)
}

function* refreshImportOfferRoutineWatcher() {
  yield takeLatest(refreshImportOfferRoutine.TRIGGER, refreshImportOffer)
}

function* makeImportOfferPublicRoutineWatcher() {
  yield takeLatest(makeImportOfferPublicRoutine.TRIGGER, makeImportOfferPublic)
}

function* clearImportFiltersRoutineWatcher() {
  yield takeLeading(clearImportFiltersRoutine.TRIGGER, clearImportFilters)
}

function* getImportFilterTabsListRoutineWatcher() {
  yield takeLatest(
    getImportFilterTabsListRoutine.TRIGGER,
    getImportFilterTabsList
  )
}

function* createImportFilterTabRoutineWatcher() {
  yield takeLatest(createImportFilterTabRoutine.TRIGGER, createImportFilterTab)
}

function* removeImportFilterTabRoutineWatcher() {
  yield takeLatest(removeImportFilterTabRoutine.TRIGGER, removeImportFilterTab)
}

function* setImportActiveTabRoutineWatcher() {
  yield takeLeading(setImportActiveTabRoutine.TRIGGER, setImportActiveTab)
}

function* listenToExchangeTabEventsWatcher(payload) {
  const channel = yield call(listenToExchangeTabEvents, payload)
  while (true) {
    const action = yield take(channel)
    yield put(action)
  }
}

function* importOffersCleanerRoutineWatcher() {
  yield takeLatest(importOffersCleanerRoutine.TRIGGER, importOffersCleaner)
}

export const exchangeImportSagas = [
  fork(getImportOffersRoutineWatcher),
  fork(clearImportOffersRoutineWatcher),
  fork(cleanImportOffersDetailsRoutineWatcher),
  fork(showImportOfferDetailsRoutineWatcher),
  fork(setImportDrawerRoutineWatcher),
  fork(setImportFiltersRoutineWatcher),
  fork(importFiltersDebounceWatcher),
  fork(updateImportOfferRoutineWatcher),
  fork(reserveImportOfferRoutineWatcher),
  fork(refreshImportOfferRoutineWatcher),
  fork(clearImportFiltersRoutineWatcher),
  fork(getImportFilterTabsListRoutineWatcher),
  fork(createImportFilterTabRoutineWatcher),
  fork(removeImportFilterTabRoutineWatcher),
  fork(setImportActiveTabRoutineWatcher),
  fork(importOffersCleanerRoutineWatcher),
  fork(bidImportOfferRoutineWatcher),
  fork(makeImportOfferPublicRoutineWatcher)
]
